import React, { useRef, useEffect, memo, useCallback, useMemo } from 'react';
import { Bot } from 'lucide-react';
import Message from './Message';
import { Message as MessageType } from '../../types';

interface MessagesListProps {
    messages: MessageType[];
    isTyping: boolean;
    onLike?: (id: number, liked: boolean | null) => void;
    onCopy?: (text: string) => void;
    onFeedback?: (id: number, text: string) => void;
}

const MessagesList: React.FC<MessagesListProps> = memo(({
    messages,
    isTyping,
    onLike,
    onCopy,
    onFeedback
}) => {
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, isTyping, scrollToBottom]);

    // 空会话引导（2026-09-17 友好性②，与离线版文案逐字一致：
    // 首屏告诉用户传什么、怎么传效果好 + 两条⚠️声明）
    // 0918 修复：chatStore 常驻欢迎语种子（messages.length 恒≥1），
    // 空态判定改"无用户消息"——否则引导永远不显示（0918 用户实测抓出）
    const isEmptyConversation = messages.every(m => m.sender !== 'user');
    const emptyGuide = isEmptyConversation ? (
        <div className="empty-guide" style={{
            margin: '24px auto', maxWidth: 560, padding: '14px 18px',
            background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
            fontSize: 13.5, lineHeight: 1.8, color: '#334155',
        }}>
            <b>📋 上传检测报告，开始 NTRK/TL118 入组初筛</b><br />
            支持 NGS / IHC / PCR / FISH 报告：<br />
            • <b>PDF</b>：直接上传原件（文字版/扫描版均可）<br />
            • <b>拍照</b>：正对纸面、避免反光和阴影，一页一张<br />
            • <b>截图</b>：保持原分辨率，勿缩放<br />
            多页报告可一次多选（或打包 zip 上传），分析约需几分钟。<br />
            <br />
            ⚠️ <b>上传资料前请务必将报告中个人信息去除（脱敏）。</b><br />
            ⚠️ 本服务仅限 NTRK 融合的判断，不包含临床条件的判断。
        </div>
    ) : null;

    // 优化的消息渲染 - 使用 useMemo 避免不必要的重新渲染
    const renderedMessages = useMemo(() => {
        return messages.map((message) => (
            <Message
                key={message.id}
                message={message}
                onLike={onLike}
                onCopy={onCopy}
                onFeedback={onFeedback}
            />
        ));
    }, [messages, onLike, onCopy, onFeedback]);

    const typingIndicator = useMemo(() => {
        if (!isTyping) return null;

        return (
            <div className="message ai-message typing">
                <div className="message-avatar">
                    <Bot size={20} />
                </div>
                <div className="message-content">
                    <div className="typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                </div>
            </div>
        );
    }, [isTyping]);

    return (
        <div className="messages-container" ref={containerRef}>
            {emptyGuide}
            {renderedMessages}
            {typingIndicator}
            <div ref={messagesEndRef} />
        </div>
    );
});

MessagesList.displayName = 'MessagesList';

export default MessagesList;